import classNames from 'classnames';
import { Identifier } from 'dnd-core';
import Immutable from 'immutable';
import $ from 'jquery';
import React from 'react';
import type { ConnectDropTarget, DropTargetConnector, DropTargetMonitor, DropTargetSpec } from 'react-dnd';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import _ from 'underscore';

import DropZoneProgress from 'sharedreact/components/DropZoneProgress';
import HiddenFileInput from 'sharedreact/components/HiddenFileInput';
import PreviewTile from 'sharedreact/components/PreviewTile';
import Spinner from 'sharedreact/components/Spinner';
import Progress from 'sharedreact/data/Progress';

const dropzoneId = 'dropzone-input-';

type DropTargetComponentProps = {
  dropItemType: Identifier | null,
  isOver: boolean,
  
  connectDropTarget: ConnectDropTarget,
};

type Props = DropTargetComponentProps & {
  className?: string,
  isLoading?: boolean,
  instruction: string,
  image?: string,
  fileTypes?: string,
  readOnly?: boolean,
  overlay?: Iterable<React.ReactNode>,
  acceptMultipleFiles?: boolean,
  dropDisabled?: boolean,
  progress?: Progress,
  language?: string,
  
  onDropFiles: (files: (FileList | File[])) => void,
  onCancel?: (progressId: string) => void,
};

type State = {
  id: string
};

class DropZone extends React.Component<Props, State> {
  constructor (props: Props) {
    super(props);

    this.state = {
      id: _.uniqueId(dropzoneId)
    };

    this.handleFileInputChange = this.handleFileInputChange.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  handleFileInputChange (name:string, files: FileList) {
    this.props.onDropFiles(files);
  }

  handleClick (event: React.MouseEvent<HTMLDivElement>) {
    $('#' + this.state.id).trigger('click');
  }

  renderDropzoneContent () {
    const { isLoading, progress, instruction, language, onCancel } = this.props;

    if (isLoading) {
      return <Spinner className='esko_dropzone-spinner' />;
    } else if (progress != null) {
      return <DropZoneProgress language={language} progress={progress} onCancel={onCancel} />;
    } else {
      return (
        <span className='esko_dropzonetext'>
          {instruction}
        </span>
      );
    }
  }

  override render (): React.ReactNode {
    const { className, isLoading, image, fileTypes, readOnly, isOver, progress,
      dropItemType, connectDropTarget, overlay, acceptMultipleFiles, dropDisabled } = this.props;

    const dragInProgress = (dropItemType === NativeTypes.FILE);
    const dropZoneClassName = classNames(
      'esko_dropzone',
      {'esko_dropzone-uploading': isLoading},
      {'esko_dropzone-highlight': dragInProgress && !isOver && !dropDisabled && progress == null},
      {'esko_dropzone-active': isOver && !dropDisabled && progress == null},
      {'esko_dropzone_pointer': !dropDisabled && progress == null}
    );

    const showDropZone = !readOnly &&
      (isLoading || !image || dragInProgress || progress != null);

    return connectDropTarget(
      <div className={className} onClick={!readOnly && progress == null ? this.handleClick : undefined} >
        <div className='esko_dropzone-inner'>
          { image &&
            <PreviewTile images={Immutable.List([image])} large>
              {overlay}
            </PreviewTile>
          }
          { showDropZone &&
            <div className={dropZoneClassName}>
              {this.renderDropzoneContent()}
            </div>
          }
        </div>
        {!readOnly && !dropDisabled && <HiddenFileInput id={this.state.id}
          onFilesChanged={this.handleFileInputChange}
          fileTypes={fileTypes}
          acceptMultipleFiles={acceptMultipleFiles}
          />
        }
      </div>
    );
  }
}

const target: DropTargetSpec<Props> = {
  drop (props: Props, monitor: DropTargetMonitor) {
    if (props.onDropFiles) {
      props.onDropFiles(monitor.getItem().files);
    }
  },
  canDrop (props: Props, monitor: DropTargetMonitor): boolean {
    return !props.dropDisabled && props.progress == null;
  }
};

function collect (connect: DropTargetConnector, monitor: DropTargetMonitor): DropTargetComponentProps {
  return {
    connectDropTarget: connect.dropTarget(),
    dropItemType: monitor.getItemType(),
    isOver: monitor.isOver()
  };
}

export default DropTarget(
  (props: Props) => props.readOnly ? [] : [ NativeTypes.FILE ],
  target,
  collect
)(DropZone);